// @ts-nocheck
// DAWA Address Autocomplete Component
// Genbrugelig komponent til adresse autosuggest via DAWA API
// Brug: new DawaAddressAutocomplete(inputElementId, options)

class DawaAddressAutocomplete {
  constructor(inputElementId, options = {}) {
    this.inputElement = document.getElementById(inputElementId);
    if (!this.inputElement) {
      console.error(`Input element with id "${inputElementId}" not found`);
      return;
    }

    // Options
    this.options = {
      minChars: options.minChars || 3,
      debounceDelay: options.debounceDelay || 300,
      onSelect: options.onSelect || null,
      placeholder: options.placeholder || "Start med at skrive din adresse...",
      maxSuggestions: options.maxSuggestions || 10,
    };

    // State
    this.suggestions = [];
    this.selectedIndex = -1;
    this.debounceTimer = null;
    this.addressSelected = false; // Track if an address has been selected

    // Create dropdown
    this.createDropdown();

    // Bind events
    this.bindEvents();

    // Set placeholder
    this.inputElement.placeholder = this.options.placeholder;

    // Disable browser autocomplete
    this.inputElement.setAttribute("autocomplete", "off");
    this.inputElement.setAttribute("autocorrect", "off");
    this.inputElement.setAttribute("autocapitalize", "off");
    this.inputElement.setAttribute("spellcheck", "false");
  }

  createDropdown() {
    // Create dropdown container
    this.dropdown = document.createElement("div");
    this.dropdown.className = "dawa-autocomplete-dropdown";
    this.dropdown.style.cssText = `
      position: absolute;
      z-index: 1000;
      background: white;
      border: 1px solid #ddd;
      border-top: none;
      border-radius: 0 0 4px 4px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      max-height: 300px;
      overflow-y: auto;
      display: none;
      width: 100%;
    `;

    // Insert after input
    this.inputElement.parentNode.style.position = "relative";
    this.inputElement.parentNode.insertBefore(
      this.dropdown,
      this.inputElement.nextSibling
    );
  }

  bindEvents() {
    // Input event for autocomplete
    this.inputElement.addEventListener("input", (e) => {
      this.handleInput(e.target.value);
    });

    // Keyboard navigation
    this.inputElement.addEventListener("keydown", (e) => {
      this.handleKeyDown(e);
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (
        !this.inputElement.contains(e.target) &&
        !this.dropdown.contains(e.target)
      ) {
        this.hideDropdown();
      }
    });

    // Prevent form submission on Enter if dropdown is open
    this.inputElement.addEventListener("keypress", (e) => {
      if (e.key === "Enter" && this.dropdown.style.display === "block") {
        e.preventDefault();
      }
    });
  }

  handleInput(value) {
    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Reset selection
    this.selectedIndex = -1;

    // If address was already selected and user starts typing, reset the flag
    if (this.addressSelected) {
      this.addressSelected = false;
    }

    // Check minimum characters
    if (value.length < this.options.minChars) {
      this.hideDropdown();
      return;
    }

    // Don't show dropdown if address was selected (unless user modified it)
    if (this.addressSelected) {
      return;
    }

    // Debounce API call
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(value);
    }, this.options.debounceDelay);
  }

  async fetchSuggestions(query) {
    try {
      const response = await fetch(
        `https://dawa.aws.dk/autocomplete?q=${encodeURIComponent(
          query
        )}&type=adresse&caretpos=${
          query.length
        }&supplerendebynavn=true&stormodtagerpostnumre=true&multilinje=false&fuzzy=`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch suggestions");
      }

      const data = await response.json();
      this.suggestions = data.slice(0, this.options.maxSuggestions);

      if (this.suggestions.length > 0) {
        this.showDropdown();
      } else {
        this.hideDropdown();
      }
    } catch (error) {
      console.error("Error fetching DAWA suggestions:", error);
      this.hideDropdown();
    }
  }

  showDropdown() {
    this.dropdown.innerHTML = "";

    this.suggestions.forEach((suggestion, index) => {
      const item = document.createElement("div");
      item.className = "dawa-autocomplete-item";
      item.style.cssText = `
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        transition: background-color 0.2s;
      `;

      // Format suggestion
      item.innerHTML = `
        <div style="font-weight: 500; color: #2c3e50;">${suggestion.tekst}</div>
        ${
          suggestion.adresse
            ? `<div style="font-size: 0.875rem; color: #7f8c8d; margin-top: 4px;">
            ${suggestion.adresse.vejnavn} ${suggestion.adresse.husnr}${
                suggestion.adresse.etage ? ", " + suggestion.adresse.etage : ""
              }${suggestion.adresse.dør ? " " + suggestion.adresse.dør : ""}, ${
                suggestion.adresse.postnr
              } ${suggestion.adresse.postnrnavn}
          </div>`
            : ""
        }
      `;

      // Hover effect
      item.addEventListener("mouseenter", () => {
        this.selectedIndex = index;
        this.highlightItem(index);
      });

      // Click to select
      item.addEventListener("click", () => {
        this.selectSuggestion(suggestion);
      });

      this.dropdown.appendChild(item);
    });

    this.dropdown.style.display = "block";
  }

  hideDropdown() {
    this.dropdown.style.display = "none";
    this.selectedIndex = -1;
  }

  highlightItem(index) {
    const items = this.dropdown.querySelectorAll(".dawa-autocomplete-item");
    items.forEach((item, i) => {
      if (i === index) {
        item.style.backgroundColor = "#f8f9fa";
      } else {
        item.style.backgroundColor = "white";
      }
    });
  }

  handleKeyDown(e) {
    if (this.dropdown.style.display !== "block") {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.suggestions.length - 1
        );
        this.highlightItem(this.selectedIndex);
        break;

      case "ArrowUp":
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.highlightItem(this.selectedIndex);
        break;

      case "Enter":
        e.preventDefault();
        if (
          this.selectedIndex >= 0 &&
          this.selectedIndex < this.suggestions.length
        ) {
          this.selectSuggestion(this.suggestions[this.selectedIndex]);
        }
        break;

      case "Escape":
        this.hideDropdown();
        break;
    }
  }

  selectSuggestion(suggestion) {
    // Format full address
    let fullAddress = suggestion.tekst;

    // If we have more detailed address info, use that
    if (suggestion.adresse) {
      const addr = suggestion.adresse;
      fullAddress = `${addr.vejnavn} ${addr.husnr}${
        addr.etage ? ", " + addr.etage : ""
      }${addr.dør ? " " + addr.dør : ""}, ${addr.postnr} ${addr.postnrnavn}`;
    }

    // Set input value
    this.inputElement.value = fullAddress;

    // Mark that an address has been selected
    this.addressSelected = true;

    // Hide dropdown
    this.hideDropdown();

    // Set focus back to input element
    this.inputElement.focus();

    // Call onSelect callback if provided
    if (this.options.onSelect && typeof this.options.onSelect === "function") {
      this.options.onSelect(suggestion);
    }

    // Trigger input event to update validation
    const event = new Event("input", { bubbles: true });
    this.inputElement.dispatchEvent(event);

    // Trigger blur event to validate the field
    const blurEvent = new Event("blur", { bubbles: true });
    this.inputElement.dispatchEvent(blurEvent);
  }

  // Public method to destroy the autocomplete
  destroy() {
    if (this.dropdown && this.dropdown.parentNode) {
      this.dropdown.parentNode.removeChild(this.dropdown);
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}

// Make it globally available
window.DawaAddressAutocomplete = DawaAddressAutocomplete;
